import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Users, Search, Check, X, Pause, DollarSign, TrendingUp, 
  ShoppingBag, Settings, Award, RefreshCw, ChevronDown, Plus, UserPlus
} from 'lucide-react';
import { AdminLayout } from '../components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminCrewPage() {
  const [activeTab, setActiveTab] = useState('members');
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [pagination, setPagination] = useState({ total: 0, skip: 0, limit: 20 });
  
  // Modal création membre
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    instagram_handle: '',
    tiktok_handle: '',
    auto_approve: true
  });
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchSettings();
  }, []);

  useEffect(() => {
    if (activeTab === 'members') fetchMembers();
    if (activeTab === 'referrals') fetchReferrals();
  }, [activeTab, filters, pagination.skip]);

  const getToken = () => localStorage.getItem('admin_token');

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/crew/admin/stats`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (response.ok) setStats(await response.json());
    } catch (error) {
      console.error('Stats error:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/crew/admin/settings`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (response.ok) setSettings(await response.json());
    } catch (error) {
      console.error('Settings error:', error);
    }
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: pagination.skip.toString(),
        limit: pagination.limit.toString(),
      });
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);

      const response = await fetch(`${API_URL}/api/crew/admin/members?${params}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (response.ok) {
        const data = await response.json();
        setMembers(data.members);
        setPagination(prev => ({ ...prev, total: data.total }));
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferrals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: pagination.skip.toString(),
        limit: pagination.limit.toString(),
      });
      if (filters.status) params.append('status', filters.status);

      const response = await fetch(`${API_URL}/api/crew/admin/referrals?${params}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (response.ok) {
        const data = await response.json();
        setReferrals(data.referrals);
        setPagination(prev => ({ ...prev, total: data.total }));
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleMemberAction = async (memberId, action, extra = {}) => {
    try {
      const response = await fetch(`${API_URL}/api/crew/admin/members/${memberId}/${action}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(extra),
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail);
      
      toast.success(data.message);
      fetchMembers();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handlePayReferral = async (referralId) => {
    const method = prompt('Méthode de paiement (virement, paypal, credit):', 'virement');
    if (!method) return;

    try {
      const response = await fetch(`${API_URL}/api/crew/admin/referrals/${referralId}/pay`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ payment_method: method }),
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail);
      
      toast.success(data.message);
      fetchReferrals();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const updateSettings = async (key, value) => {
    try {
      const response = await fetch(`${API_URL}/api/crew/admin/settings`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [key]: value }),
      });
      
      if (response.ok) {
        setSettings(prev => ({ ...prev, [key]: value }));
        toast.success('Paramètre mis à jour');
      }
    } catch (error) {
      toast.error('Erreur de mise à jour');
    }
  };

  // Créer un nouveau membre
  const handleCreateMember = async (e) => {
    e.preventDefault();
    
    if (!createForm.first_name.trim() || !createForm.last_name.trim() || !createForm.email.trim()) {
      toast.error('Prénom, nom et email sont requis');
      return;
    }
    
    setIsCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/crew/admin/members/create`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(createForm),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Membre créé! Code promo: ${data.member.promo_code}`);
        setShowCreateModal(false);
        setCreateForm({
          first_name: '',
          last_name: '',
          email: '',
          instagram_handle: '',
          tiktok_handle: '',
          auto_approve: true
        });
        fetchMembers();
        fetchStats();
      } else {
        toast.error(data.detail || 'Erreur de création');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setIsCreating(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-500',
      active: 'bg-green-500/20 text-green-500',
      suspended: 'bg-red-500/20 text-red-500',
      rejected: 'bg-zinc-500/20 text-gray-200',
      confirmed: 'bg-blue-500/20 text-blue-500',
      due: 'bg-purple-500/20 text-purple-500',
      paid: 'bg-green-500/20 text-green-500',
    };
    return styles[status] || 'bg-zinc-500/20 text-gray-200';
  };

  return (
    <AdminLayout title="Mutagim Crew" subtitle="Programme d'affiliation">
      <div data-testid="admin-crew-page">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <Users className="w-8 h-8 text-[#C0000C]" />
                Mutagim Crew
              </h1>
              <p className="text-gray-400">Programme d'affiliation</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-[#C0000C] hover:bg-[#A0000A]"
                data-testid="create-member-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Ajouter un membre
              </Button>
              <Button
                onClick={() => { fetchStats(); activeTab === 'members' ? fetchMembers() : fetchReferrals(); }}
                variant="outline"
                className="border-zinc-700 text-white hover:text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
            </div>
          </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-5 h-5 text-blue-500" />
                <span className="text-white text-sm">Membres actifs</span>
              </div>
              <div className="text-2xl font-bold text-white" data-testid="active-members">{stats.members.active}</div>
              <div className="text-gray-200 text-sm">{stats.members.pending} en attente</div>
            </div>

            <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-5 h-5 text-green-500" />
                <span className="text-white text-sm">CA total</span>
              </div>
              <div className="text-2xl font-bold text-white">{stats.all_time.revenue.toFixed(0)} ₪</div>
            </div>

            <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-[#C0000C]" />
                <span className="text-white text-sm">Commissions totales</span>
              </div>
              <div className="text-2xl font-bold text-white">{stats.all_time.commission.toFixed(0)} ₪</div>
            </div>

            <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <ShoppingBag className="w-5 h-5 text-purple-500" />
                <span className="text-white text-sm">Ventes ce mois</span>
              </div>
              <div className="text-2xl font-bold text-white">{stats.this_month.sales}</div>
              <div className="text-gray-200 text-sm">{stats.this_month.revenue.toFixed(0)} ₪</div>
            </div>

            <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <Award className="w-5 h-5 text-yellow-500" />
                <span className="text-white text-sm">ROI</span>
              </div>
              <div className="text-2xl font-bold text-green-500">{stats.roi_percent}%</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-zinc-800 pb-4">
          {['members', 'referrals', 'settings'].map(tab => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'ghost'}
              onClick={() => { setActiveTab(tab); setPagination(p => ({ ...p, skip: 0 })); }}
              className={activeTab === tab ? 'bg-[#C0000C] hover:bg-[#A0000A]' : 'text-white'}
              data-testid={`tab-${tab}`}
            >
              {tab === 'members' && 'Membres'}
              {tab === 'referrals' && 'Commissions'}
              {tab === 'settings' && <><Settings className="w-4 h-4 mr-2" />Paramètres</>}
            </Button>
          ))}
        </div>

        {/* Members Tab */}
        {activeTab === 'members' && (
          <>
            {/* Filters */}
            <div className="flex gap-4 mb-6">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-200" />
                <Input
                  placeholder="Rechercher..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10 bg-zinc-900/50 border-zinc-700 text-white"
                  data-testid="search-members"
                />
              </div>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="bg-zinc-900/50 border border-zinc-700 rounded-md px-3 text-white"
                data-testid="filter-status"
              >
                <option value="">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="active">Actifs</option>
                <option value="suspended">Suspendus</option>
                <option value="rejected">Rejetés</option>
              </select>
            </div>

            {/* Members Table */}
            <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-zinc-900/50">
                  <tr className="text-white text-sm">
                    <th className="text-left p-4">Membre</th>
                    <th className="text-left p-4">Code</th>
                    <th className="text-left p-4">Niveau</th>
                    <th className="text-right p-4">Ventes</th>
                    <th className="text-right p-4">CA</th>
                    <th className="text-right p-4">Commission due</th>
                    <th className="text-left p-4">Statut</th>
                    <th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="p-8 text-center text-gray-200">Chargement...</td></tr>
                  ) : members.length === 0 ? (
                    <tr><td colSpan={8} className="p-8 text-center text-gray-200">Aucun membre trouvé</td></tr>
                  ) : members.map(member => (
                    <tr key={member.id} className="border-t border-zinc-800 hover:bg-zinc-800/30">
                      <td className="p-4">
                        <div>
                          <div className="text-white font-medium">{member.first_name} {member.last_name}</div>
                          <div className="text-gray-200 text-sm">{member.email}</div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="font-mono text-[#C0000C]">{member.promo_code}</span>
                      </td>
                      <td className="p-4 text-white">{member.level_label}</td>
                      <td className="p-4 text-right text-white">{member.sales_count}</td>
                      <td className="p-4 text-right text-white">{member.total_revenue.toFixed(0)} ₪</td>
                      <td className="p-4 text-right text-green-500">{member.confirmed_commission.toFixed(0)} ₪</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(member.status)}`}>
                          {member.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          {member.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleMemberAction(member.id, 'approve')}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`approve-${member.id}`}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleMemberAction(member.id, 'reject', { reason: 'Non conforme' })}
                                className="border-red-600 text-red-500 hover:bg-red-600/20"
                                data-testid={`reject-${member.id}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {member.status === 'active' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMemberAction(member.id, 'suspend', { reason: 'Suspension admin' })}
                              className="border-yellow-600 text-yellow-500 hover:bg-yellow-600/20"
                            >
                              <Pause className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <>
            <div className="flex gap-4 mb-6">
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="bg-zinc-900/50 border border-zinc-700 rounded-md px-3 text-white"
              >
                <option value="">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="confirmed">Confirmées</option>
                <option value="due">À payer</option>
                <option value="paid">Payées</option>
              </select>
            </div>

            <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-zinc-900/50">
                  <tr className="text-white text-sm">
                    <th className="text-left p-4">Commande</th>
                    <th className="text-right p-4">Montant</th>
                    <th className="text-right p-4">Commission</th>
                    <th className="text-left p-4">Source</th>
                    <th className="text-left p-4">Statut</th>
                    <th className="text-left p-4">Date</th>
                    <th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} className="p-8 text-center text-gray-200">Chargement...</td></tr>
                  ) : referrals.length === 0 ? (
                    <tr><td colSpan={7} className="p-8 text-center text-gray-200">Aucune commission</td></tr>
                  ) : referrals.map(ref => (
                    <tr key={ref.id} className="border-t border-zinc-800 hover:bg-zinc-800/30">
                      <td className="p-4 text-white font-mono">{ref.order_number}</td>
                      <td className="p-4 text-right text-white">{ref.order_total.toFixed(0)} ₪</td>
                      <td className="p-4 text-right text-green-500">{ref.commission_amount.toFixed(0)} ₪</td>
                      <td className="p-4">
                        <span className={`text-xs ${ref.source === 'promo_code' ? 'text-[#C0000C]' : 'text-blue-400'}`}>
                          {ref.source === 'promo_code' ? 'Code promo' : 'Cookie 30j'}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(ref.status)}`}>
                          {ref.status}
                        </span>
                      </td>
                      <td className="p-4 text-white text-sm">
                        {new Date(ref.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="p-4">
                        {(ref.status === 'confirmed' || ref.status === 'due') && (
                          <Button
                            size="sm"
                            onClick={() => handlePayReferral(ref.id)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <DollarSign className="w-4 h-4 mr-1" />
                            Payer
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && settings && (
          <div className="max-w-2xl">
            <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-6 space-y-6">
              <h3 className="text-xl font-bold text-white mb-4">Paramètres du programme</h3>
              
              <div className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg">
                <div>
                  <Label className="text-white">Validation automatique</Label>
                  <p className="text-gray-200 text-sm">Activer les nouveaux membres sans validation manuelle</p>
                </div>
                <Switch
                  checked={settings.auto_approve}
                  onCheckedChange={(checked) => updateSettings('auto_approve', checked)}
                  data-testid="auto-approve-switch"
                />
              </div>

              <div className="p-4 bg-zinc-900/50 rounded-lg">
                <Label className="text-white">Réduction client (%)</Label>
                <p className="text-gray-200 text-sm mb-2">Réduction appliquée avec un code Crew</p>
                <Input
                  type="number"
                  min="1"
                  max="50"
                  value={settings.default_discount_percent}
                  onChange={(e) => updateSettings('default_discount_percent', parseInt(e.target.value))}
                  className="w-24 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div className="p-4 bg-zinc-900/50 rounded-lg">
                <Label className="text-white">Délai de confirmation (jours)</Label>
                <p className="text-gray-200 text-sm mb-2">Jours avant confirmation de commission</p>
                <Input
                  type="number"
                  min="1"
                  max="90"
                  value={settings.confirmation_days}
                  onChange={(e) => updateSettings('confirmation_days', parseInt(e.target.value))}
                  className="w-24 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div className="p-4 bg-zinc-900/50 rounded-lg">
                <Label className="text-white">Niveaux de commission</Label>
                <div className="mt-4 space-y-2">
                  {Object.entries(settings.levels || {}).map(([key, level]) => (
                    <div key={key} className="flex items-center justify-between text-sm">
                      <span className="text-white">{level.label} ({level.min_sales}-{level.max_sales} ventes)</span>
                      <span className="text-white font-bold">{(level.commission_rate * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pagination */}
        {(activeTab === 'members' || activeTab === 'referrals') && pagination.total > pagination.limit && (
          <div className="flex justify-center gap-2 mt-6">
            <Button
              variant="outline"
              disabled={pagination.skip === 0}
              onClick={() => setPagination(p => ({ ...p, skip: Math.max(0, p.skip - p.limit) }))}
              className="border-zinc-700 text-white"
            >
              Précédent
            </Button>
            <span className="flex items-center px-4 text-white">
              {Math.floor(pagination.skip / pagination.limit) + 1} / {Math.ceil(pagination.total / pagination.limit)}
            </span>
            <Button
              variant="outline"
              disabled={pagination.skip + pagination.limit >= pagination.total}
              onClick={() => setPagination(p => ({ ...p, skip: p.skip + p.limit }))}
              className="border-zinc-700 text-white"
            >
              Suivant
            </Button>
          </div>
        )}
        </div>
        
        {/* Modal création membre */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-[#C0000C]" />
                  Nouveau membre Crew
                </h2>
                <button 
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleCreateMember} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white text-sm">Prénom *</Label>
                    <Input
                      value={createForm.first_name}
                      onChange={(e) => setCreateForm({...createForm, first_name: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      placeholder="Jean"
                      data-testid="create-first-name"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-sm">Nom *</Label>
                    <Input
                      value={createForm.last_name}
                      onChange={(e) => setCreateForm({...createForm, last_name: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      placeholder="Dupont"
                      data-testid="create-last-name"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-white text-sm">Email *</Label>
                  <Input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    placeholder="jean@example.com"
                    data-testid="create-email"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white text-sm">Instagram</Label>
                    <Input
                      value={createForm.instagram_handle}
                      onChange={(e) => setCreateForm({...createForm, instagram_handle: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      placeholder="@username"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-sm">TikTok</Label>
                    <Input
                      value={createForm.tiktok_handle}
                      onChange={(e) => setCreateForm({...createForm, tiktok_handle: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      placeholder="@username"
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-3 pt-2">
                  <Switch
                    checked={createForm.auto_approve}
                    onCheckedChange={(c) => setCreateForm({...createForm, auto_approve: c})}
                  />
                  <Label className="text-white text-sm">Activer immédiatement (générer le code promo)</Label>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 border-zinc-700 text-white"
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    disabled={isCreating}
                    className="flex-1 bg-[#C0000C] hover:bg-[#A0000A]"
                    data-testid="submit-create-member"
                  >
                    {isCreating ? 'Création...' : 'Créer le membre'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminCrewPage;
